import axios from "@/utils/axios";
import type { FieldFile } from "@sdt/render-form-engine";
import type {
  AssignmentUploadsStatus,
  UploadRequestConfig,
} from "@/queries/uploads";
import type { RequestFormResponse } from "@/types/common/form";
import {
  useQuery,
  useMutation,
  useQueryClient,
  QueryClient,
} from "@tanstack/react-query";

interface GetFormParams {
  id: string;
  relationships: string[];
  formalityId: string;
}

const DEFAULT_FORM_RELATIONSHIPS = [
  "authApplicantFormAssignments",
  "authApplicantFormAssignments.executionNode.flowExecution.flow.process",
  "formAssignments",
];

export const useGetFormById = (params: GetFormParams, enabled = true) => {
  const { id, formalityId: formality } = params;

  const relationshipsNotEmpty = params.relationships?.length > 0;

  const relationships = relationshipsNotEmpty
    ? [params.relationships.join("."), ...DEFAULT_FORM_RELATIONSHIPS]
    : [];

  const queryKey = {
    id,
    formality,
    relationships,
  };

  const queryParams = {
    filter: { formality },
    ...(relationshipsNotEmpty && { relationships: relationships.join(",") }),
  };

  return useQuery<RequestFormResponse>({
    queryKey: ["get-form-by-id", queryKey],
    queryFn: async (): Promise<RequestFormResponse> => {
      const { data } = await axios.get(`/internal/forms/${id}`, {
        params: queryParams,
      });

      return {
        data: data.data,
      };
    },
    enabled: enabled && !!id,
  });
};

interface SaveFormParams {
  assignmentId: string;
  fields: Record<string, string>;
  is_final_submission: boolean;
  step_id?: string;
  save_as_draft?: boolean;
}

export const invalidateInternalFormQueries = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: ["tramite-forms"] });
  queryClient.invalidateQueries({ queryKey: ["form-detail"] });
  queryClient.removeQueries({ queryKey: ["get-form-by-id"] });
};

export const useSaveDraftForm = () => {
  return useMutation({
    mutationFn: async (data: {
      assignmentId: string;
      fields: Record<string, unknown>;
    }) => {
      const { assignmentId, fields } = data;
      const response = await axios.post(
        `/admin/v1/internal/forms/assignments/${assignmentId}/submit`,
        { fields, is_final_submission: false, save_as_draft: true },
      );
      return response.data;
    },
    gcTime: 0,
  });
};

export const useSaveForm = (options?: { skipInvalidation?: boolean }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["save-form"],
    mutationFn: async (data: Partial<SaveFormParams>) => {
      const { assignmentId, fields, is_final_submission } = data;

      if (!assignmentId) {
        throw new Error("assignmentId es requerido");
      }

      const response = await axios.post(
        `/admin/v1/internal/forms/assignments/${assignmentId}/submit`,
        { fields, is_final_submission },
      );

      return response.data;
    },
    onSuccess: () => {
      if (!options?.skipInvalidation) {
        invalidateInternalFormQueries(queryClient);
      }
    },
  });
};

// ---------------- Documentos ----------------

const isFieldFile = (value: unknown): value is FieldFile =>
  !!value &&
  typeof value === "object" &&
  typeof (value as FieldFile).id === "string" &&
  typeof (value as FieldFile).url === "string";

/**
 * El adjunto y el borrado responden `{ message, files }`, con `files` como la
 * lista completa de archivos que le quedan al campo. Lo que no tenga esa forma
 * da `[]`.
 */
const extractFieldFiles = (body: unknown): FieldFile[] => {
  const files = (body as { files?: unknown } | null)?.files;
  return Array.isArray(files) ? files.filter(isFieldFile) : [];
};

/**
 * Adjunta subidas a un campo `file` de la asignación, sumándolas a las que ya
 * tenía (`append`). El adjunto confirma la subida si aún no lo estaba. En un
 * campo de archivo único, el backend reemplaza el archivo igualmente.
 * @param assignmentId El ID de la asignación.
 * @param fieldId El ID del campo de tipo `file`.
 * @param uploadIds Los IDs de las subidas que se van a adjuntar.
 * @param config Configuración adicional para la solicitud HTTP.
 * @returns Todos los archivos del campo después del adjunto.
 */
export const attachUploadsToField = async (
  assignmentId: string,
  fieldId: string,
  uploadIds: string[],
  config: UploadRequestConfig = {},
): Promise<FieldFile[]> => {
  const { data } = await axios.post(
    `/admin/v1/internal/forms/assignments/${assignmentId}/fields/${fieldId}/upload`,
    { upload_ids: uploadIds, append: true },
    {
      ...config,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    },
  );
  return extractFieldFiles(data);
};

export interface FieldValidationResult {
  valid: boolean;
  message: string;
}

export const validateFieldFiles = async (
  assignmentId: string,
  fieldId: string,
  config: UploadRequestConfig & { signal?: AbortSignal } = {},
): Promise<FieldValidationResult> => {
  const { data } = await axios.post<{ data: FieldValidationResult }>(
    `/admin/v1/internal/forms/assignments/${assignmentId}/fields/${fieldId}/validate`,
    undefined,
    { ...config, headers: { Accept: "application/json" } },
  );
  return data.data;
};

/**
 * Quita un archivo ya adjunto a un campo. El borrado es definitivo y sólo se
 * admite mientras el formulario se llena (fuera de eso responde 422).
 * @param assignmentId El ID de la asignación.
 * @param fieldId El ID del campo de tipo `file`.
 * @param mediaUuid El `id` del archivo en el campo.
 * @param config Configuración adicional para la solicitud HTTP.
 * @returns Los archivos que le quedan al campo.
 */
export const deleteFieldFile = async (
  assignmentId: string,
  fieldId: string,
  mediaUuid: string,
  config: UploadRequestConfig = {},
): Promise<FieldFile[]> => {
  const { data } = await axios.delete(
    `/admin/v1/internal/forms/assignments/${assignmentId}/fields/${fieldId}/files/${mediaUuid}`,
    {
      ...config,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    },
  );
  return extractFieldFiles(data);
};

/**
 * Qué subidas de la asignación siguen en curso (PENDING/UPLOADING sin vencer).
 * Con alguna en curso, el backend rechaza el envío definitivo con 422.
 */
export const getAssignmentUploadsStatus = async (
  assignmentId: string,
  config: UploadRequestConfig = {},
): Promise<AssignmentUploadsStatus> => {
  const { data } = await axios.get<{ data: AssignmentUploadsStatus }>(
    `/admin/v1/internal/forms/assignments/${assignmentId}/uploads`,
    { ...config, headers: { Accept: "application/json" } },
  );
  return data.data;
};

// Asignar formulario a usuario
export const useAssignMe = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { form_id: string }) => {
      const { data } = await axios.post(
        `/admin/v1/internal/forms/assign/me`,
        payload,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tramite-forms"] });
      queryClient.invalidateQueries({ queryKey: ["form-detail"] });
      queryClient.invalidateQueries({ queryKey: ["get-form-by-id"] });
    },
  });
};

// obtener form assignments
export const useGetFormAssignments = () => {
  return useQuery({
    queryKey: ["get-form-assignments"],
    queryFn: async () => {
      const { data } = await axios.get("/admin/v1/internal/forms/assignments");
      return data;
    },
  });
};

// obtener assignment por id
export const useGetAssignmentById = (assignmentId: string) => {
  return useQuery({
    queryKey: ["get-assignment-by-id", assignmentId],
    queryFn: async () => {
      const { data } = await axios.get(
        `/admin/v1/internal/forms/assignments/${assignmentId}`,
      );
      return data;
    },
    enabled: !!assignmentId,
  });
};

// Iniciar formulario

export const useStartForm = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (assignmentId: string) => {
      const { data } = await axios.post(
        `/admin/v1/internal/forms/assignments/${assignmentId}/start`,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tramite-forms"] });
      queryClient.invalidateQueries({ queryKey: ["form-detail"] });
      queryClient.invalidateQueries({ queryKey: ["get-form-by-id"] });
    },
  });
};
